import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { deleteMedia, fetchVideos } from "../../features/admin/MediaSlice";
import { toast } from "react-toastify";
import { PlayCircleIcon } from "@heroicons/react/16/solid";
import { PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import VideoModal from "../../components/admin/VideoModal";
import ApiCustom from "../../utils/ApiCustom";

const getUserPermissions = () => {
  const permissions = localStorage.getItem("permissions");
  return permissions
    ? JSON.parse(permissions).map((p) => p.permissionSlug)
    : [];
};

const userId = localStorage.getItem("userId");
const hasPermission = (requiredPermission) => {
  if (userId == 1) {
    return true;
  }
  const userPermissions = getUserPermissions();
  return userPermissions.includes(requiredPermission);
};

const CATEGORY_TAB_PREFIX = "category-";
const MEDIA_CATEGORY_TYPE = "video";

const slugify = (value) => {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/g, "d")
    .replace(/Đ/g, "D")
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
};

const Video = () => {
  const dispatch = useDispatch();
  const { medias, status, error } = useSelector((state) => state.medias);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [filteredVideos, setFilteredVideos] = useState([]);
  const [currentTab, setCurrentTab] = useState("video");

  const [mediaCategories, setMediaCategories] = useState([]);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryName, setCategoryName] = useState("");
  const [categorySaving, setCategorySaving] = useState(false);
  const [deleteCategory, setDeleteCategory] = useState(null);
  const [categoryError, setCategoryError] = useState("");

  const currentCategoryId = currentTab.startsWith(CATEGORY_TAB_PREFIX)
    ? currentTab.replace(CATEGORY_TAB_PREFIX, "")
    : null;

  const fetchMediaCategories = async () => {
    try {
      const response = await ApiCustom.get("/media-category-shows", {
        params: {
          limit: 1000,
          type: MEDIA_CATEGORY_TYPE,
        },
      });

      const data = response.data?.data?.data || [];

      setMediaCategories(
        Array.isArray(data)
          ? data.filter((category) => category.type === MEDIA_CATEGORY_TYPE)
          : []
      );
    } catch (error) {
      toast.error("Không tải được danh mục video");
    }
  };

  const handleOpenModal = (media = null) => {
    setSelectedMedia(media);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedMedia(null);
    dispatch(fetchVideos());
  };

  const handleDelete = (id) => {
    dispatch(deleteMedia(id)).then((res) => {
      if (res.meta.requestStatus === "fulfilled") {
        toast.success("Xóa video thành công!");
        handleCloseModal();
      } else {
        toast.error("Đã xảy ra lỗi khi xóa video!");
      }
    });
  };

  const handleOpenCategoryModal = (category = null) => {
    setEditingCategory(category);
    setCategoryName(category?.name || "");
    setCategoryError("");
    setIsCategoryModalOpen(true);
  };

  const handleCloseCategoryModal = () => {
    setIsCategoryModalOpen(false);
    setEditingCategory(null);
    setCategoryName("");
    setCategorySaving(false);
    setCategoryError("");
  };

  const handleSaveCategory = async (e) => {
    e.preventDefault();

    const name = categoryName.trim();

    if (!name) {
      setCategoryError("Tên danh mục video không được để trống");
      return;
    }

    const payload = {
      name,
      slug: slugify(name),
      type: MEDIA_CATEGORY_TYPE,
      status: editingCategory?.status ?? true,
    };

    try {
      setCategorySaving(true);

      if (editingCategory) {
        await ApiCustom.put(
          `/media-category-update/${editingCategory.id}`,
          payload,
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        toast.success("Cập nhật danh mục video thành công");
      } else {
        await ApiCustom.post("/media-category-create", payload, {
          headers: {
            "Content-Type": "application/json",
          },
        });

        toast.success("Thêm danh mục video thành công");
      }

      handleCloseCategoryModal();
      fetchMediaCategories();
    } catch (error) {
      toast.error(
        error.response?.data?.messages ||
          error.response?.data?.message ||
          "Lưu danh mục video thất bại"
      );
    } finally {
      setCategorySaving(false);
    }
  };

  const handleConfirmDeleteCategory = async () => {
    if (!deleteCategory) return;

    try {
      await ApiCustom.delete(`/media-category-delete/${deleteCategory.id}`);

      if (currentTab === `${CATEGORY_TAB_PREFIX}${deleteCategory.id}`) {
        setCurrentTab("video");
      }

      toast.success("Xóa danh mục video thành công");
      setDeleteCategory(null);
      fetchMediaCategories();
      dispatch(fetchVideos());
    } catch (error) {
      toast.error(
        error.response?.data?.messages ||
          error.response?.data?.message ||
          "Xóa danh mục video thất bại"
      );
    }
  };

  useEffect(() => {
    dispatch(fetchVideos());
    fetchMediaCategories();
  }, [dispatch]);

  useEffect(() => {
    if (status === "idle") {
      dispatch(fetchVideos());
    }
  }, [status, dispatch]);

  useEffect(() => {
    const filter = medias?.filter((video) => {
      if (currentTab === "video") {
        return !video.mediaCategoryId;
      }

      if (currentCategoryId) {
        return Number(video.mediaCategoryId) === Number(currentCategoryId);
      }

      return false;
    });

    setFilteredVideos(filter);
  }, [medias, currentTab, currentCategoryId]);

  if (status === "loading") return <p>Loading...</p>;
  if (status === "failed") return <p>Error: {error}</p>;

  return (
    <div className="p-6">
      <style>
        {`
          .media-category-tabs::-webkit-scrollbar {
            height: 4px;
          }

          .media-category-tabs::-webkit-scrollbar-track {
            background: transparent;
          }

          .media-category-tabs::-webkit-scrollbar-thumb {
            background: #f8fafc;
            border-radius: 9999px;
          }

          .media-category-tabs::-webkit-scrollbar-thumb:hover {
            background: #e5e7eb;
          }

          .media-category-tabs {
            scrollbar-width: thin;
            scrollbar-color: #f8fafc transparent;
          }
        `}
      </style>

      <h1 className="text-2xl font-bold mb-4">Quản lý video</h1>

      {hasPermission("MEDIA_UPLOAD") && (
        <button
          onClick={() => handleOpenModal()}
          className="btn btn-success transition-colors px-4 py-2 rounded mb-4"
        >
          + Thêm video
        </button>
      )}

      <div className="border-b border-gray-200 mb-4">
        <div className="-mb-px flex items-stretch gap-3">
          <nav className="media-category-tabs flex space-x-4 overflow-x-auto flex-1">
            <button
              onClick={() => setCurrentTab("video")}
              className={`whitespace-nowrap h-14 px-1 border-b-2 flex items-center flex-shrink-0 ${
                currentTab === "video"
                  ? "border-blue-500 text-blue-500"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              Video
            </button>

            {mediaCategories.map((category) => {
              const tabKey = `${CATEGORY_TAB_PREFIX}${category.id}`;

              return (
                <div
                  key={category.id}
                  onClick={() => setCurrentTab(tabKey)}
                  className={`group whitespace-nowrap h-14 px-1 border-b-2 flex items-center gap-2 cursor-pointer flex-shrink-0 ${
                    currentTab === tabKey
                      ? "border-blue-500 text-blue-500"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  <span>{category.name}</span>

                  <span className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {hasPermission("MEDIA_UPDATE") && (
                      <button
                        type="button"
                        title="Sửa danh mục"
                        className="w-7 h-7 flex items-center justify-center rounded-md border border-blue-200 bg-blue-50 text-blue-600 hover:bg-blue-100"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenCategoryModal(category);
                        }}
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                    )}

                    {hasPermission("MEDIA_DELETE") && (
                      <button
                        type="button"
                        title="Xóa danh mục"
                        className="w-7 h-7 flex items-center justify-center rounded-md border border-red-200 bg-red-50 text-red-600 hover:bg-red-100"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteCategory(category);
                        }}
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    )}
                  </span>
                </div>
              );
            })}
          </nav>

          {hasPermission("MEDIA_CREATE") && (
            <button
              type="button"
              onClick={() => handleOpenCategoryModal()}
              className="h-12 px-2 mb-2 button btn btn-success rounded-md whitespace-nowrap flex-shrink-0 text-sm text-black font-medium"
            >
              + Thêm danh mục video
            </button>
          )}
        </div>
      </div>

      {filteredVideos?.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredVideos.map((video, index) => (
            <div
              key={video.id || index}
              className="card bg-base-100 shadow-xl hover:shadow-2xl transition-all"
            >
              <figure
                className="relative aspect-video cursor-pointer group"
                onClick={() => handleOpenModal(video)}
              >
                <iframe
                  src={video?.url?.replace("watch?v=", "embed/")}
                  title={video.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                ></iframe>

                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all">
                  <PlayCircleIcon className="w-12 h-12 text-white" />
                </div>
              </figure>

              <div className="card-body">
                <h2 className="card-title line-clamp-2">{video.title}</h2>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-center text-gray-500 mt-20">
          Chưa có video được tải lên
        </p>
      )}

      {isModalOpen && (
        <VideoModal
          media={selectedMedia}
          mediaCategoryId={currentCategoryId}
          onClose={handleCloseModal}
          onDelete={() => handleDelete(selectedMedia?.id)}
        />
      )}

      {isCategoryModalOpen && (
        <div className="fixed inset-0 z-50 bg-gray-500 bg-opacity-50 flex justify-center items-center">
          <div className="bg-base-100 p-6 rounded-md w-96">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold mb-4">
                {editingCategory
                  ? "Chỉnh sửa danh mục video"
                  : "Tạo danh mục video"}
              </h2>

              <button
                type="button"
                onClick={handleCloseCategoryModal}
                className="px-4 py-2 border rounded-md shadow-sm text-sm font-medium text-base-700 bg-base-100 hover:bg-base-200"
                disabled={categorySaving}
              >
                X
              </button>
            </div>

            <form onSubmit={handleSaveCategory}>
              <label className="block text-sm mb-2 font-medium">
                Tên danh mục video<i className="text-red-600"> *</i>
              </label>

              <input
                type="text"
                placeholder="Nhập danh mục video"
                value={categoryName}
                onChange={(e) => {
                  setCategoryError("");
                  setCategoryName(e.target.value);
                }}
                className="input input-bordered w-full mb-4"
                disabled={categorySaving}
              />

              {categoryError && (
                <p className="text-red-500 text-sm mb-4">{categoryError}</p>
              )}

              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={handleCloseCategoryModal}
                  disabled={categorySaving}
                >
                  Hủy
                </button>

                <button
                  type="submit"
                  className="btn btn-primary btn-sm"
                  disabled={categorySaving}
                >
                  {categorySaving
                    ? "Đang lưu..."
                    : editingCategory
                    ? "Cập nhật"
                    : "Tạo"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteCategory && (
        <div className="fixed inset-0 z-50 bg-gray-500 bg-opacity-50 flex justify-center items-center">
          <div className="bg-base-100 p-6 rounded-md w-96">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold mb-4">
                Xóa danh mục video
              </h2>

              <button
                type="button"
                onClick={() => setDeleteCategory(null)}
                className="px-4 py-2 border rounded-md shadow-sm text-sm font-medium text-base-700 bg-base-100 hover:bg-base-200"
              >
                X
              </button>
            </div>

            <p className="mb-6">
              Bạn có chắc muốn xóa danh mục video{" "}
              <strong>{deleteCategory.name}</strong> không?
            </p>

            <div className="flex justify-end space-x-4">
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => setDeleteCategory(null)}
              >
                Hủy
              </button>

              <button
                type="button"
                className="btn btn-error btn-sm"
                onClick={handleConfirmDeleteCategory}
              >
                Xóa
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Video;